import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { AskDataMode } from '../../settings/types/AskDataMode';
import { IDataSourceDetailInfo, NlpSetting } from '../models/IDataSourceInfo';
import { Module } from '../Module';
import { DataSources } from '../../content/resources/Datasources';
import { Sites } from '../resources/Sites';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ToasterService, ToastType } from '../services/ToasterService';

interface DataSourceNlpSettingEditorControls {
  enterEditMode: () => void;
  exitEditMode: () => void;
  save: ($event: JQueryEventObject) => void;
}

interface DataSourceNlpSettingEditorState {
  canEdit: boolean;
  isBusy: boolean;
  isEditMode: boolean;
  isAuthoringEnabled: boolean;
  isDirty: () => boolean;
}

interface DataSourceNlpSettingModel {
  askDataStatusLabels: _.Dictionary<string>;
  isEnabledMode: boolean;
  isSiteDefaultMode: boolean;
  currentAskDataStatus: string;
  analysisOption: NlpSetting;
  displayTextForViewMode: string;
  displayTextForErrorMode: string;
  siteDefaultDisplayTextForEditMode: string;
}

interface DataSourceNlpSettingEditorScope extends ng.IScope {
  datasource: IDataSourceDetailInfo;
  canSetNlpSetting: boolean;
  controls: DataSourceNlpSettingEditorControls;
  onSave: ($event: JQueryEventObject) => void;
  state: DataSourceNlpSettingEditorState;
  masterModel: DataSourceNlpSettingModel;
  model: DataSourceNlpSettingModel;
  updateCurrentAskDataStatus: (value: string) => void;
}

Module.instance().directive('tbDataSourceNlpSettingEditor', [
  'Sites',
  (Sites: Sites,
   ) => {
    return {
      restrict: 'A',
      replace: true,
      templateUrl: 'dataSourceNlpSettingEditor.html',
      scope: {
        datasource: '<',
        canSetNlpSetting: '<',
        onSave: '<',
      },
      link: (scope: DataSourceNlpSettingEditorScope) => {

        // initialize the UI state
        scope.state = {
          canEdit:    scope.canSetNlpSetting,
          isBusy:     false,
          isEditMode: false,
          isAuthoringEnabled: false,
          isDirty:    () => _.isEqual(scope.masterModel, scope.model),
        };
        scope.model = {
          isEnabledMode: false,
          isSiteDefaultMode: scope.datasource.nlpSetting === NlpSetting.siteDefault,
          analysisOption: (scope.datasource.nlpSetting === NlpSetting.disabled) ? NlpSetting.enabled : scope.datasource.nlpSetting,
          displayTextForViewMode: '',
          siteDefaultDisplayTextForEditMode: '',
          currentAskDataStatus: NlpSetting.siteDefault.toString(),
          askDataStatusLabels: {},
          displayTextForErrorMode:'',
        };

        scope.state.isAuthoringEnabled = getSessionManagementServiceInstance().canSessionUserAuthorOnWeb();
        if (!scope.state.isAuthoringEnabled) {
          scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_error_state_NonAuthoring();
          scope.state.canEdit = false;
        } else  {
          Sites.getCurrentSiteSettingsForSiteAdmin()
          .then((data: any) => {
            if (data.errors && data.errors.length){
              // User is not a Site Admin, so we can't know if the "site default" is "enabled" or "disabled".
              setUpModelForNonSiteAdmin(scope);
            } else {
              setupModelForSiteAdmin(data, scope);
            }
          });

          scope.model.askDataStatusLabels[NlpSetting.enabled.toString()] = Messages.dataSourceNlpSetting_option_enabled();
          scope.model.askDataStatusLabels[NlpSetting.disabled.toString()] = Messages.dataSourceNlpSetting_option_disabled();
          scope.model.askDataStatusLabels[NlpSetting.siteDefault.toString()] = Messages.dataSourceNlpSetting_option_siteDefault();

          setupModelForAuthoring(scope);
        }

        scope.updateCurrentAskDataStatus = (value: string) => {
          scope.model.currentAskDataStatus = value;
          scope.model.isEnabledMode = value === NlpSetting.enabled.toString();
          scope.model.isSiteDefaultMode = value === NlpSetting.siteDefault.toString();
          if (value === NlpSetting.enabled.toString()) {
            scope.model.analysisOption = NlpSetting.enabled;
          }
        };

        // controls for user actions
        scope.controls = {
          enterEditMode: () => {
            scope.state.isEditMode = true;
            scope.masterModel = _.clone(scope.model);
          },
          exitEditMode: () => {
            scope.state.isEditMode = false;
            scope.model = scope.masterModel;
          },
          save: ($event: JQueryEventObject) => {
            scope.state.isBusy = true;
            const params = {
              id: scope.datasource.id,
              nlpOption: (scope.model.isEnabledMode) ? scope.model.analysisOption : scope.model.currentAskDataStatus,
            };
            DataSources.setNlpSetting(params).then((result: IResult) => {
              if (result.errors) { // unexpected server error
                ToasterService.instance.pop({ type: ToastType.Error, text: Messages.dataSourceNlpSetting_failed() });
              } else { // successful setCertification
                ToasterService.instance.pop({ type: ToastType.Info, text: Messages.dataSourceNlpSetting_success() });
                scope.state.isEditMode = false;
                scope.onSave($event);
              }
              scope.state.isBusy = false;
            });
          },
        };
      }
    };
  }
]);

function setUpModelForNonSiteAdmin(scope: DataSourceNlpSettingEditorScope) {
  switch (scope.datasource.nlpSetting) {
    case NlpSetting.enabled:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_enabled();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      scope.model.analysisOption = NlpSetting.enabled;
      break;
    case NlpSetting.disableIndex:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_indexDisabled();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      scope.model.analysisOption = NlpSetting.disableIndex;
      break;
    case NlpSetting.refresh24Hrs:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_timedRefresh();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      scope.model.analysisOption = NlpSetting.refresh24Hrs;
      break;
    case NlpSetting.disabled:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_disabled();
      scope.model.currentAskDataStatus = NlpSetting.disabled.toString();
      scope.model.isEnabledMode = false;
      break;
    case NlpSetting.siteDefault:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_siteDefault();
      scope.model.currentAskDataStatus = NlpSetting.siteDefault.toString();
      scope.model.siteDefaultDisplayTextForEditMode = Messages.dataSourceNlpSetting_option_siteDefault_message();
      scope.model.isEnabledMode = false;
      break;
  }
}

function setupModelForSiteAdmin(data: any, scope: DataSourceNlpSettingEditorScope) {
  switch (data.siteAdminSettings.askDataMode) {
    case AskDataMode.ENABLED_BY_DEFAULT:
      if (scope.datasource.nlpSetting === NlpSetting.siteDefault) {
        scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_siteDefault_enabled();
      }
      scope.model.siteDefaultDisplayTextForEditMode = Messages.dataSourceNlpSetting_option_siteDefault_message();
      scope.model.askDataStatusLabels[NlpSetting.siteDefault.toString()] = Messages.dataSourceNlpSetting_option_siteDefault_enabled();
      break;
    case AskDataMode.DISABLED_BY_DEFAULT:
      if (scope.datasource.nlpSetting === NlpSetting.siteDefault) {
        scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_siteDefault_disabled();
      }
      scope.model.askDataStatusLabels[NlpSetting.siteDefault.toString()] = Messages.dataSourceNlpSetting_option_siteDefault_disabled();
      break;
    case AskDataMode.DISABLED_ALWAYS:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_DisabledAlways();
      scope.state.canEdit = false;
      break;
  }
}

function setupModelForAuthoring(scope: DataSourceNlpSettingEditorScope) {
  switch(scope.datasource.nlpSetting) {
    case NlpSetting.enabled:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_enabled();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      break;
    case NlpSetting.disableIndex:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_indexDisabled();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      break;
    case NlpSetting.refresh24Hrs:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_timedRefresh();
      scope.model.currentAskDataStatus = NlpSetting.enabled.toString();
      scope.model.isEnabledMode = true;
      break;
    case NlpSetting.disabled:
      scope.model.displayTextForViewMode = Messages.dataSourceNlpSetting_disabled();
      scope.model.currentAskDataStatus = NlpSetting.disabled.toString();
      break;
    case NlpSetting.siteDefault:
      scope.model.displayTextForViewMode = '';
      scope.model.currentAskDataStatus = NlpSetting.siteDefault.toString();
      break;
  }
}

